/**
 * Pure helper for Logger.error - parses args into normalized format for Sentry.
 * No external dependencies; fully testable.
 * Uses CommonJS for Node compatibility (server, html-package).
 */

function getRequestUrl(err) {
  const requestUrl = err?.config?.url;
  const baseUrl = err?.config?.baseURL;

  if (!requestUrl) {
    return baseUrl;
  }

  if (!baseUrl) {
    return requestUrl;
  }

  try {
    const normalizedBaseUrl = `${String(baseUrl).replace(/\/+$/, "")}/`;
    return new URL(
      String(requestUrl).replace(/^\/+/, ""),
      normalizedBaseUrl
    ).toString();
  } catch (e) {
    return requestUrl;
  }
}

function extractErrorDetails(err) {
  const requestUrl = getRequestUrl(err);

  return {
    message: err.message,
    name: err.name,
    stack: err.stack,
    ...(err.response && {
      responseStatus: err.response.status,
      responseData: err.response.data,
    }),
    ...(requestUrl && { requestUrl }),
  };
}

function getOriginalError(...logs) {
  for (const log of logs) {
    const error = [log, log?.cause, log?.error].find(
      (value) => value instanceof Error
    );
    if (error instanceof Error) {
      return error;
    }
  }

  return undefined;
}

function createErrorCaptureGuard() {
  const reportedTransportErrors = new WeakSet();

  return (error, { reportedTransportError = false, isCause = false } = {}) => {
    if (!(error instanceof Error)) {
      return true;
    }

    if (isCause && reportedTransportErrors.has(error)) {
      return false;
    }

    if (reportedTransportError) {
      reportedTransportErrors.add(error);
    }
    return true;
  };
}

function normalizeRequestUrlForGrouping(requestUrl) {
  if (!requestUrl) {
    return "unknown-url";
  }

  const url = String(requestUrl);

  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (e) {
    return url.split("#")[0].split("?")[0] || "unknown-url";
  }
}

function normalizeFingerprintValue(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._:/-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getRequestErrorKind(rawDetails = {}) {
  if (rawDetails.responseStatus != null) {
    return `status-${rawDetails.responseStatus}`;
  }

  const message = String(rawDetails.message || "").toLowerCase();

  if (message.includes("timeout")) {
    return "timeout";
  }

  if (message.includes("aborted") || message.includes("abort")) {
    return "request-aborted";
  }

  if (message.includes("network")) {
    return "network-error";
  }

  return rawDetails.name
    ? `error-${normalizeFingerprintValue(rawDetails.name)}`
    : "request-error";
}

function buildRequestErrorGrouping(rawDetails, requestEndpointOverride) {
  if (
    !requestEndpointOverride &&
    (!rawDetails ||
      (rawDetails.requestUrl == null && rawDetails.responseStatus == null))
  ) {
    return null;
  }

  const requestEndpoint = normalizeRequestUrlForGrouping(
    requestEndpointOverride || rawDetails?.requestUrl
  );
  const errorKind = getRequestErrorKind(rawDetails);
  const tags = {
    request_endpoint: requestEndpoint,
    request_error_kind: errorKind,
  };

  if (rawDetails?.responseStatus != null) {
    tags.response_status = String(rawDetails.responseStatus);
  }

  return {
    fingerprint: ["logger-error", "api-request", requestEndpoint, errorKind],
    tags,
  };
}

function isOptionsObject(obj) {
  if (obj == null || typeof obj !== "object" || obj instanceof Error) {
    return false;
  }
  const o = obj;
  return (
    o.tags != null ||
    o.extra != null ||
    o.title != null ||
    o.cause instanceof Error ||
    o.requestEndpoint != null ||
    o.reportedTransportError === true
  );
}

function getObjectErrorTitle(value) {
  if (value == null || typeof value !== "object") {
    return String(value);
  }

  if (value.message) {
    return String(value.message);
  }

  if (value.errorMsg && value.errorCode) {
    return `${value.errorCode}: ${value.errorMsg}`;
  }

  if (value.errorMsg) {
    return String(value.errorMsg);
  }

  if (value.error) {
    return String(value.error);
  }

  return String(value);
}

/**
 * Build normalized error report from Logger.error arguments.
 *
 * @param {*} message - Primary error message (string, Error, or object)
 * @param {*} [optionsOrRest] - Options object { tags, extra, title } OR legacy data
 * @param {...*} rest - Additional args (legacy)
 * @returns {{ title: string, tags: Object, customExtra: Object, fingerprint?: string[] }} Normalized report for Sentry
 */
function buildErrorReport(message, optionsOrRest, ...rest) {
  const isOptions = isOptionsObject(optionsOrRest);
  const opts = isOptions ? optionsOrRest : null;
  const originalError = getOriginalError(message, optionsOrRest, ...rest);

  const title =
    isOptions && opts?.title
      ? String(opts.title)
      : typeof message === "string"
      ? message
      : getObjectErrorTitle(message);

  const tags = isOptions && opts?.tags ? { ...opts.tags } : {};
  const customExtra = isOptions && opts?.extra ? { ...opts.extra } : {};

  if (!isOptions) {
    if (message instanceof Error) {
      customExtra.raw = extractErrorDetails(message);
    } else if (message != null && typeof message === "object") {
      customExtra.primary = message;
    }
  }

  if (!isOptions) {
    if (optionsOrRest !== undefined) {
      if (optionsOrRest instanceof Error) {
        customExtra.raw = extractErrorDetails(optionsOrRest);
      } else if (typeof optionsOrRest === "object") {
        customExtra.context = optionsOrRest;
      } else {
        customExtra.raw = optionsOrRest;
      }
    }
    if (rest.length > 0) {
      customExtra.additional = rest;
    }
  }

  const groupingDetails = originalError
    ? extractErrorDetails(originalError)
    : customExtra.raw;
  const requestErrorGrouping = buildRequestErrorGrouping(
    groupingDetails,
    opts?.requestEndpoint
  );

  if (requestErrorGrouping) {
    Object.assign(tags, requestErrorGrouping.tags);
  }

  return {
    title,
    tags,
    customExtra,
    fingerprint: requestErrorGrouping?.fingerprint,
  };
}

/**
 * Check if error message should be ignored (not sent to Sentry).
 */
function isErrorIgnored(errorMessage, ignorePatterns) {
  return ignorePatterns.some((pattern) => errorMessage.includes(pattern));
}

module.exports = {
  buildErrorReport,
  createErrorCaptureGuard,
  getOriginalError,
  isErrorIgnored,
};
